
import React, { useState } from "react";
import { useToast } from "@/components/ui/use-toast";
import { Lock, User, Mail, Shield, Check } from "lucide-react";
import InputField from "../ui-elements/input-field";
import Button from "../ui-elements/button";
import { supabase } from "@/integrations/supabase/client";
import { useTranslation } from "react-i18next";
import { Checkbox } from "@/components/ui/checkbox";
import { Link } from "react-router-dom";

export interface SignUpFormProps {
  onToggleForm: () => void;
}

const SignUpForm: React.FC<SignUpFormProps> = ({ onToggleForm }) => {
  const { t } = useTranslation(['auth', 'privacy', 'common']);
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    firstName: "",
    lastName: "",
  });
  const [consent, setConsent] = useState({
    marketing: false,
    analytics: false,
    necessary: true, // Always required
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error when user types
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleConsentChange = (type: 'marketing' | 'analytics', checked: boolean) => {
    setConsent(prev => ({
      ...prev,
      [type]: checked
    }));
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.email) {
      newErrors.email = t('auth:required');
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = t('auth:invalidEmail');
    }
    
    if (!formData.password) {
      newErrors.password = t('auth:required');
    } else if (formData.password.length < 6) {
      newErrors.password = t('auth:passwordMinLength');
    }
    
    if (!formData.firstName) {
      newErrors.firstName = t('auth:required');
    }
    
    if (!formData.lastName) {
      newErrors.lastName = t('auth:required');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsLoading(true);
    try {
      // Sign up the user with Supabase
      const { data, error } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          data: {
            first_name: formData.firstName,
            last_name: formData.lastName,
            consent_settings: consent
          },
        },
      });
      
      if (error) throw error;
      
      toast({
        title: t('common:success'),
        description: t('auth:signUpSuccess'),
        duration: 5000,
      });
      
      console.log("Sign up successful:", data);
      onToggleForm(); // Switch back to login view
    } catch (error: any) {
      console.error("Sign up error:", error);
      toast({
        title: t('common:error'),
        description: error.message || t('auth:errorSignUp'),
        variant: "destructive",
        duration: 3000,
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSignUp} className="space-y-5">
      <InputField
        label={t('auth:firstName')}
        id="firstName"
        name="firstName"
        placeholder={t('auth:firstNamePlaceholder')}
        icon={<User className="h-4 w-4" />}
        value={formData.firstName}
        onChange={handleChange}
        error={errors.firstName}
        autoComplete="given-name"
        required
      />
      
      <InputField
        label={t('auth:lastName')}
        id="lastName"
        name="lastName"
        placeholder={t('auth:lastNamePlaceholder')}
        icon={<User className="h-4 w-4" />}
        value={formData.lastName}
        onChange={handleChange}
        error={errors.lastName}
        autoComplete="family-name"
        required
      />

      <InputField
        label={t('auth:email')}
        type="email"
        id="email"
        name="email"
        placeholder={t('auth:emailPlaceholder')}
        icon={<Mail className="h-4 w-4" />}
        value={formData.email}
        onChange={handleChange}
        error={errors.email}
        autoComplete="email"
        required
      />
      
      <InputField
        label={t('auth:password')}
        id="password"
        name="password"
        placeholder={t('auth:passwordPlaceholder')}
        icon={<Lock className="h-4 w-4" />}
        value={formData.password}
        onChange={handleChange}
        error={errors.password}
        showPasswordToggle
        autoComplete="new-password"
        required
      />
      
      <div className="space-y-3 pt-2">
        <div className="text-sm font-medium">{t('privacy:dataConsent')}</div>
        
        <div className="flex items-center space-x-2">
          <Checkbox 
            id="marketing-consent" 
            checked={consent.marketing} 
            onCheckedChange={(checked) => handleConsentChange('marketing', checked === true)}
            className="rounded-sm"
          />
          <label
            htmlFor="marketing-consent"
            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            {t('privacy:marketingConsent')}
          </label>
        </div>
        <div className="text-xs text-muted-foreground ml-6">
          {t('privacy:marketingConsentDesc')}
        </div>
        
        <div className="flex items-center space-x-2">
          <Checkbox 
            id="analytics-consent" 
            checked={consent.analytics} 
            onCheckedChange={(checked) => handleConsentChange('analytics', checked === true)}
            className="rounded-sm"
          />
          <label
            htmlFor="analytics-consent"
            className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            {t('privacy:analyticsConsent')}
          </label>
        </div>
        <div className="text-xs text-muted-foreground ml-6">
          {t('privacy:analyticsConsentDesc')}
        </div>
      </div>
      
      <div className="text-xs text-muted-foreground mt-4">
        {t('auth:agreeToTerms')}{" "}
        <Link to="/privacy" className="text-[#E4002B] hover:underline font-medium">
          {t('auth:privacyPolicy')}
        </Link>{" "}
        {t('common:and')}{" "}
        <Link to="/terms" className="text-[#E4002B] hover:underline font-medium">
          {t('auth:termsOfService')}
        </Link>
      </div>
      
      <Button
        type="submit"
        variant="primary"
        size="lg"
        fullWidth
        isLoading={isLoading}
        className="mt-8 bg-[#E4002B] hover:bg-[#c7001e]"
      >
        {t('auth:joinClub')}
      </Button>

      <div className="text-center mt-8 text-sm text-muted-foreground">
        <p>
          {t('auth:alreadyMember')}{" "}
          <button 
            onClick={onToggleForm} 
            className="text-[#E4002B] hover:underline font-medium"
            type="button"
          >
            {t('auth:signIn')}
          </button>
        </p>
      </div>
    </form>
  );
};

export default SignUpForm;
